/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Model Image Studio Lightbox Inspector Modal (Task 10.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useEffect, useState } from 'react';
import { useImageStudioStore } from '../../stores/useImageStudioStore';
import { useInpaintingStore } from '../../stores/useInpaintingStore';
import { useImageOpsStore } from '../../stores/useImageOpsStore';
import { useI18nStore } from '../../stores/useI18nStore';
import {
  X,
  Heart,
  Download,
  Copy,
  Check,
  RotateCcw,
  Zap,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Brush,
  Scissors,
  Wand2,
  ArrowUpRight,
} from 'lucide-react';

interface ImageLightboxModalProps {
  onNavigateToInpaint?: () => void;
  onNavigateToOps?: () => void;
}

export const ImageLightboxModal: React.FC<ImageLightboxModalProps> = ({
  onNavigateToInpaint,
  onNavigateToOps,
}) => {
  const { t } = useI18nStore();
  const {
    activeLightboxImage,
    closeLightbox,
    toggleFavoriteImage,
    reusePrompt,
  } = useImageStudioStore();

  const [copiedPrompt, setCopiedPrompt] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && activeLightboxImage) {
        closeLightbox();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeLightboxImage, closeLightbox]);

  if (!activeLightboxImage) return null;

  const handleCopyPrompt = () => {
    navigator.clipboard.writeText(activeLightboxImage.prompt);
    setCopiedPrompt(true);
    setTimeout(() => setCopiedPrompt(false), 2000);
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = activeLightboxImage.url;
    link.download = `sophia-art-${activeLightboxImage.id}.jpg`;
    link.target = '_blank';
    link.rel = 'noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleReuse = () => {
    reusePrompt(activeLightboxImage);
    closeLightbox();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 bg-black/80 backdrop-blur-md animate-in fade-in duration-200">
      <div className="relative w-full max-w-6xl h-[90vh] rounded-3xl border border-surface-border bg-surface overflow-hidden shadow-2xl flex flex-col lg:flex-row">
        {/* Close Button Top Right */}
        <button
          type="button"
          onClick={closeLightbox}
          className="absolute top-4 right-4 z-20 w-9 h-9 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-colors"
          title={t('common.close')}
        >
          <X className="w-5 h-5" />
        </button>

        {/* 1. Left: Image Canvas Viewport (7 cols) */}
        <div className="flex-1 lg:w-7/12 h-1/2 lg:h-full bg-surface-elevated flex flex-col justify-between p-4 relative overflow-hidden select-none">
          {/* Zoom & Viewport Controls Top Left */}
          <div className="flex items-center gap-1.5 z-10">
            <button
              type="button"
              onClick={() => setZoomLevel((z) => Math.min(z + 0.25, 2.5))}
              className="p-2 rounded-xl bg-black/50 hover:bg-black/70 text-white text-xs flex items-center gap-1 transition-colors"
              title={t('media.viewFull')}
            >
              <ZoomIn className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel((z) => Math.max(z - 0.25, 0.75))}
              className="p-2 rounded-xl bg-black/50 hover:bg-black/70 text-white text-xs flex items-center gap-1 transition-colors"
              title={t('common.close')}
            >
              <ZoomOut className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setZoomLevel(1)}
              className="p-2 rounded-xl bg-black/50 hover:bg-black/70 text-white text-xs flex items-center gap-1 transition-colors"
              title={t('media.resetDefaults')}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <span className="text-[10px] font-mono text-slate-400 bg-black/40 px-2 py-1 rounded-lg">
              {Math.round(zoomLevel * 100)}%
            </span>
          </div>

          {/* Centered Image with Smooth Zoom */}
          <div className="flex-1 flex items-center justify-center overflow-auto p-2">
            <img
              src={activeLightboxImage.url}
              alt={activeLightboxImage.prompt}
              style={{ transform: `scale(${zoomLevel})` }}
              className="max-w-full max-h-full object-contain rounded-xl shadow-2xl transition-transform duration-200"
            />
          </div>

          {/* Quick Action Pills Bottom */}
          <div className="flex items-center justify-between z-10 pt-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => toggleFavoriteImage(activeLightboxImage.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold backdrop-blur-md transition-colors ${
                  activeLightboxImage.isFavorite
                    ? 'bg-rose-500 text-white'
                    : 'bg-white/10 hover:bg-white/20 text-white'
                }`}
              >
                <Heart className={`w-3.5 h-3.5 ${activeLightboxImage.isFavorite ? 'fill-white' : ''}`} />
                <span>{activeLightboxImage.isFavorite ? t('templates.favorites') : t('templates.addToFavorites')}</span>
              </button>

              <button
                type="button"
                onClick={handleDownload}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-white/10 hover:bg-white/20 text-white backdrop-blur-md transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                <span>{t('media.downloadImage')}</span>
              </button>
            </div>

            <span className="text-[11px] font-mono text-slate-400">
              {activeLightboxImage.width} x {activeLightboxImage.height} px
            </span>
          </div>
        </div>

        {/* 2. Right: Metadata Inspector Sidebar (5 cols) */}
        <div className="lg:w-5/12 h-1/2 lg:h-full p-6 overflow-y-auto space-y-6 flex flex-col justify-between border-t lg:border-t-0 lg:border-l border-surface-border bg-surface">
          <div className="space-y-5">
            {/* Model & Header */}
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-accent/15 text-accent border border-accent/20">
                  {activeLightboxImage.modelName}
                </span>
                <span className="text-xs font-mono text-foreground-muted">
                  {activeLightboxImage.aspectRatio}
                </span>
              </div>
              <h3 className="font-heading font-extrabold text-lg text-foreground">
                {t('media.lightboxTitle')}
              </h3>
            </div>

            {/* Prompt Box */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-xs font-bold text-foreground uppercase tracking-wider">
                  {t('media.prompt')}
                </label>
                <button
                  type="button"
                  onClick={handleCopyPrompt}
                  className="flex items-center gap-1 text-[11px] text-primary hover:underline font-semibold"
                >
                  {copiedPrompt ? (
                    <>
                      <Check className="w-3 h-3 text-emerald-500" />
                      <span className="text-emerald-500">{t('common.copied')}</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3 h-3" />
                      <span>{t('common.copy')}</span>
                    </>
                  )}
                </button>
              </div>

              <div className="p-3.5 rounded-xl border border-surface-border bg-surface-elevated text-xs leading-relaxed text-foreground select-text font-sans">
                {activeLightboxImage.prompt}
              </div>
            </div>

            {/* Negative Prompt if exists */}
            {activeLightboxImage.negativePrompt && (
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-foreground-muted uppercase tracking-wider">
                  {t('media.negativePrompt')}
                </label>
                <div className="p-2.5 rounded-lg border border-surface-border bg-surface-elevated/50 text-[11px] text-foreground-muted select-text">
                  {activeLightboxImage.negativePrompt}
                </div>
              </div>
            )}

            {/* Technical Parameters Grid */}
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-0.5">
                <span className="text-[10px] text-foreground-muted uppercase font-bold">{t('media.seed')}</span>
                <p className="font-mono text-xs font-bold text-foreground">
                  {activeLightboxImage.seed}
                </p>
              </div>

              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-0.5">
                <span className="text-[10px] text-foreground-muted uppercase font-bold">{t('common.cost')}</span>
                <p className="font-mono text-xs font-bold text-primary flex items-center gap-1">
                  <Zap className="w-3 h-3" />
                  <span>{activeLightboxImage.creditsUsed} {t('common.credits')}</span>
                </p>
              </div>

              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-0.5">
                <span className="text-[10px] text-foreground-muted uppercase font-bold">{t('media.stylePresetLabel')}</span>
                <p className="text-xs font-bold text-foreground capitalize">
                  {activeLightboxImage.style}
                </p>
              </div>

              <div className="p-3 rounded-xl border border-surface-border bg-surface-elevated space-y-0.5">
                <span className="text-[10px] text-foreground-muted uppercase font-bold">{t('common.status')}</span>
                <p className="text-xs text-foreground font-medium">
                  {new Date(activeLightboxImage.createdAt).toLocaleTimeString()}
                </p>
              </div>
            </div>
          </div>

          {/* Bottom Action Buttons */}
          <div className="space-y-2 pt-4 border-t border-surface-border">
            <button
              type="button"
              onClick={handleReuse}
              className="w-full py-2.5 px-4 rounded-xl border border-primary/30 bg-primary/10 text-primary hover:bg-primary/20 text-xs font-bold flex items-center justify-center gap-2 transition-colors shadow-sm"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              <span>{t('templates.useTemplate')}</span>
            </button>

            {onNavigateToInpaint && (
              <button
                type="button"
                onClick={() => {
                  if (activeLightboxImage) {
                    useInpaintingStore
                      .getState()
                      .setSourceImage(
                        activeLightboxImage.url,
                        activeLightboxImage.width,
                        activeLightboxImage.height
                      );
                  }
                  closeLightbox();
                  onNavigateToInpaint();
                }}
                className="w-full py-2.5 px-4 rounded-xl bg-primary text-primary-fg hover:opacity-90 text-xs font-bold flex items-center justify-center gap-2 transition-opacity shadow-sm"
              >
                <Brush className="w-3.5 h-3.5" />
                <span>{t('media.inpaintAction')}</span>
                <ArrowUpRight className="w-3.5 h-3.5" />
              </button>
            )}

            {onNavigateToOps && (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => {
                    if (activeLightboxImage) {
                      useImageOpsStore.getState().setSourceImage(activeLightboxImage.url);
                      useImageOpsStore.getState().setActiveTab('upscale');
                    }
                    closeLightbox();
                    onNavigateToOps();
                  }}
                  className="py-2.5 px-3 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                >
                  <Wand2 className="w-3.5 h-3.5 text-primary" />
                  <span>{t('imageOps.upscaleTab')}</span>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    if (activeLightboxImage) {
                      useImageOpsStore.getState().setSourceImage(activeLightboxImage.url);
                      useImageOpsStore.getState().setActiveTab('remove_bg');
                    }
                    closeLightbox();
                    onNavigateToOps();
                  }}
                  className="py-2.5 px-3 rounded-xl bg-surface-elevated hover:bg-muted border border-surface-border text-foreground text-xs font-semibold flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                >
                  <Scissors className="w-3.5 h-3.5 text-accent" />
                  <span>{t('imageOps.removeBgTab')}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
